import { db } from "../../../../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/**
 * Normaliza e verifica se uma string de entidade/instituição pertence à Unifesp.
 */
export const isUnifespInstitution = (nameOrId) => {
  if (!nameOrId) return false;
  const str = String(nameOrId).trim().toLowerCase();
  return (
    str.includes("unifesp") ||
    str.includes("universidade federal de são paulo") ||
    str.includes("carboplant") ||
    str.includes("carbo plant") ||
    str.includes("lab.carboplant@gmail.com") ||
    str === "qfvapxkiguqmelqakinoecpeoy63"
  );
};

/**
 * Determina se a árvore/muda e o usuário têm permissão para acessar o recurso de Coletas Científicas.
 * Regras:
 * 1. A árvore DEVE ser confidencial ou muda institucional Unifesp.
 * 2. Administradores globais sempre têm acesso.
 * 3. Usuários pro_universidade Unifesp ou pro_tecnico vinculados à Unifesp.
 * 4. Usuários de outras universidades/entidades NÃO têm acesso.
 */
export const canAccessTreeSampling = ({
  user,
  userRole,
  isAdmin,
  tree,
  isPrivateTree,
  userProfile,
  supervisorProfile,
}) => {
  if (!user || !tree) return false;

  // Administrador global sempre pode auditar e gerenciar
  if (isAdmin || userRole === "admin") return true;

  // Verifica se a árvore/muda pertence ou está sob custódia da Unifesp / Carboplant
  const treeIsUnifesp =
    tree.currentHolderId === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
    tree.ownerGestorId === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
    tree.userId === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
    tree.createdBy === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
    tree.recipientUserId === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
    tree.currentHolderClientId === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
    isUnifespInstitution(tree.currentHolderName) ||
    isUnifespInstitution(tree.ownerGestorName) ||
    isUnifespInstitution(tree.groupName) ||
    isUnifespInstitution(tree.institutionName) ||
    isUnifespInstitution(tree.proCityName) ||
    (tree.seedlingCode && String(tree.seedlingCode).toUpperCase().includes("UNIFESP"));

  // 1. Obrigatoriamente confidencial ou muda institucional Unifesp
  const isConfidential = Boolean(
    isPrivateTree ||
    tree.isPrivateTree ||
    tree.isPrivate ||
    tree.visibility === "organization" ||
    tree.pro_confidential ||
    treeIsUnifesp
  );
  if (!isConfidential) return false;

  // Caso A: Usuário é pro_universidade da Unifesp
  if (userRole === "pro_universidade") {
    const userIsUnifesp =
      user.uid === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
      user.email === "lab.carboplant@gmail.com" ||
      isUnifespInstitution(userProfile?.userName) ||
      isUnifespInstitution(userProfile?.universityName) ||
      isUnifespInstitution(userProfile?.customDisplayName) ||
      isUnifespInstitution(userProfile?.displayName) ||
      isUnifespInstitution(userProfile?.companyName);

    return userIsUnifesp && (treeIsUnifesp || tree.userId === user.uid || tree.ownerGestorId === user.uid || tree.currentHolderId === user.uid);
  }

  // Caso B: Usuário é pro_tecnico vinculado à Unifesp
  if (userRole === "pro_tecnico") {
    const supervisorIsUnifesp =
      userProfile?.parentProViveiroId === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
      supervisorProfile?.uid === "qFvaPXkigUQmeLQAkinOecPeoY63" ||
      isUnifespInstitution(supervisorProfile?.universityName) ||
      isUnifespInstitution(supervisorProfile?.displayName) ||
      isUnifespInstitution(supervisorProfile?.customDisplayName) ||
      isUnifespInstitution(userProfile?.universityName);

    return supervisorIsUnifesp || treeIsUnifesp;
  }

  // Qualquer outra instituição ou perfil padrão: sem acesso
  return false;
};

/**
 * Chave padronizada para o cache de dados meteorológicos diários.
 */
export const getMeteorologicalCacheKey = (institutionName = "unifesp", dateStr) => {
  const cleanInst = isUnifespInstitution(institutionName) ? "unifesp" : String(institutionName).toLowerCase().replace(/[^a-z0-9]/g, "_");
  const cleanDate = (dateStr || new Date().toISOString().slice(0, 10)).trim();
  return `${cleanInst}_${cleanDate}`;
};

/**
 * Busca dados meteorológicos previamente carregados para a mesma instituição e data.
 */
export const fetchMeteorologicalCache = async (institutionName = "unifesp", dateStr) => {
  try {
    const cacheKey = getMeteorologicalCacheKey(institutionName, dateStr);
    const cacheDocRef = doc(db, "meteorological_cache", cacheKey);
    const snap = await getDoc(cacheDocRef);
    if (snap.exists()) {
      return snap.data();
    }
    return null;
  } catch (error) {
    console.warn("Aviso ao buscar cache meteorológico:", error);
    return null;
  }
};

/**
 * Salva ou atualiza os dados meteorológicos do dia no cache compartilhado.
 */
export const saveMeteorologicalCache = async (institutionName = "unifesp", dateStr, cacheData) => {
  try {
    const cacheKey = getMeteorologicalCacheKey(institutionName, dateStr);
    const cacheDocRef = doc(db, "meteorological_cache", cacheKey);
    await setDoc(cacheDocRef, {
      ...cacheData,
      cacheKey,
      institution: isUnifespInstitution(institutionName) ? "unifesp" : institutionName,
      date: dateStr,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.warn("Aviso ao salvar cache meteorológico:", error);
  }
};
